import React, { useEffect, useRef, useState } from "react";
import I18n from 'i18n.js.erb'
import DateCalendar, { dateKey, formatDateKey } from "./DateCalendar";
import SubmissionListWithdrawalSelect from "./SubmissionListWithdrawalSelect";
import { isDefined } from "../../utils/utils";


const t = (key) => I18n.t(`js.submissionListWithdrawalPage.callback.${key}`);

const pad = (value) => String(value).padStart(2, '0');

// Il customer support richiama solo in un giorno lavorativo successivo a oggi: niente sabati e
// domeniche, niente giorni di chiusura della casa di grading né festività del suo paese, che
// arrivano dal server come MM-GG (Company#non_working_days)
const validCallbackDay = (date, nonWorkingDays) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);

  return day > today &&
         ![0, 6].includes(day.getDay()) &&
         !nonWorkingDays.includes(`${pad(day.getMonth() + 1)}-${pad(day.getDate())}`);
};


// Nel campo del telefono restano solo cifre, spazi, trattini, barre e il + del prefisso internazionale
// in testa: il resto (lettere, punti e parentesi di un numero incollato) viene scartato mentre si
// scrive. Le cifre a larghezza piena delle tastiere giapponesi diventano prima quelle normali
const sanitizePhone = (value) => {
  const cleaned = value.normalize('NFKC').replace(/[^\d+ \-/]/g, '').replace(/\s+/g, ' ').trimStart();

  return cleaned.charAt(0) + cleaned.slice(1).replace(/\+/g, '');
};

// Da 8 a 15 cifre, prefisso compreso: 15 è il massimo di un numero internazionale (E.164). Lo
// stesso controllo è ripetuto dal server (submission_lists#create_withdrawal_callback)
const validPhone = (value) => {
  const digits = value.replace(/\D/g, '').length;

  return digits >= 8 && digits <= 15;
};


// Stessa pillola etichettata dei campi del modulo (Figma 305:1009): pastiglia grigia col nome
// del campo e, dentro la shell, il controllo senza bordo
const LabeledField = ({ label, error, errorMessage, children }) => {

  return <div className="slx-form__field">
    <div className={`slx-form__readonly ${error ? 'slx-form__readonly--error' : ''}`}>
      <span className="slx-form__readonly-label">{label}</span>
      {children}
    </div>
    {error && <p className="slx-form__error">{errorMessage || I18n.t('js.submissionListWithdrawalPage.requiredField')}</p>}
  </div>
};


// Scelta del giorno con lo stesso calendario del filtro per data delle richieste di recesso nel
// backoffice, ma a data singola: niente titolo, modalità, azzeramento né APPLICA. Il giorno toccato
// diventa subito quello del campo e la tendina si chiude
const DayPicker = ({ value, nonWorkingDays, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;

    const onMouseDown = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false) };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  const selectDay = (selection) => {
    onChange(new Date(`${selection[0]}T00:00:00`));
    setOpen(false);
  };

  return <div className="slx-day-picker" ref={ref}>
    <button type="button"
            className={`slx-form__labeled-input slx-day-picker__toggle ${value ? '' : 'slx-form__control--placeholder'}`}
            onClick={() => setOpen(!open)}>
      {value ? formatDateKey(dateKey(value)) : t('dayPlaceholder')}
    </button>

    {
      open &&
        <div className="slx-day-picker__popover">
          <DateCalendar mode="single" selection={value ? [dateKey(value)] : []} setSelection={selectDay}
                        isDayDisabled={(date) => !validCallbackDay(date, nonWorkingDays)} />
        </div>
    }
  </div>
};


// Figma 305:1005: prima di recedere per un problema col ritiro il cliente può chiedere di essere
// ricontattato dal customer support, che di solito lo risolve programmando un altro ritiro
const SubmissionListWithdrawalCallbackModal = ({ show, submissionListId, timeSlots, nonWorkingDays, onClose, onDone }) => {

  const [day, setDay] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!show) return;

    const onKeyDown = (event) => { if (event.key === 'Escape') onClose() };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  const clearError = (field) => setErrors((previous) => ({ ...previous, [field]: false }));

  // Il calendario disabilita i giorni in cui non possiamo richiamare: il controllo qui resta per
  // un popup lasciato aperto fino al giorno dopo, quando la data scelta non sarebbe più futura
  const validate = () => {
    const invalid = {
      day: !isDefined(day),
      invalid_day: isDefined(day) && !validCallbackDay(day, nonWorkingDays || []),
      time_slot: !isDefined(timeSlot),
      phone: !isDefined(phone.trim()),
      invalid_phone: isDefined(phone.trim()) && !validPhone(phone)
    };

    setErrors(invalid);
    return !Object.values(invalid).some(Boolean);
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    setSubmitError('');

    // Il server non salva nulla: avvisa il customer support e conferma al cliente, entrambi via mail
    fetch(`/submission_lists/${submissionListId}/withdrawal_callback`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        // AAAA-MM-GG costruito dai componenti locali della data: toISOString la sposterebbe in UTC,
        // cioè al giorno prima per chi sta a est di Greenwich
        day: dateKey(day),
        time_slot: timeSlot,
        phone_number: phone.trim()
      })
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw Error(data.message);

        onDone({ day, timeSlot });
      })
      .catch((error) => {
        console.error('ERROR:', error);
        setSubmitError(error.message || t('errors.generic'));
        setSubmitting(false);
      });
  };

  return (
    <div className="slx-modal" onClick={onClose}>
      {/* il click sul contenuto non deve chiudere la modale, solo quello sullo sfondo */}
      <form className="slx-modal__dialog slx-callback" role="dialog" aria-modal="true" noValidate
            onClick={(event) => event.stopPropagation()} onSubmit={onSubmit}>
        <div className="slx-form__section">
          <div className="slx-callback__header">
            <h2 className="slx-form__section-title">{t('title')}</h2>
            <button type="button" className="slx-callback__close" aria-label="close" onClick={onClose} />
          </div>
          <p className="slx-form__section-description">{t('description')}</p>
        </div>

        <div className="slx-form__fields">
          <div className="slx-form__row">
            <LabeledField label={t('day')} error={errors.day || errors.invalid_day}
                          errorMessage={errors.invalid_day ? t('invalidDay') : null}>
              <DayPicker
                value={day}
                nonWorkingDays={nonWorkingDays || []}
                onChange={(date) => { setDay(date); setErrors((previous) => ({ ...previous, day: false, invalid_day: false })) }}
              />
            </LabeledField>

            <LabeledField label={t('timeSlot')} error={errors.time_slot}>
              <SubmissionListWithdrawalSelect
                value={timeSlot}
                options={(timeSlots || []).map((slot) => ({ value: slot, label: slot }))}
                placeholder={I18n.t('js.submissionListWithdrawalPage.choose')}
                toggleClassName="slx-form__labeled-input slx-form__labeled-input--select"
                onChange={(slot) => { setTimeSlot(slot); clearError('time_slot') }}
              />
            </LabeledField>
          </div>

          <div className="slx-form__row">
            <LabeledField label={t('phone')} error={errors.phone || errors.invalid_phone}
                          errorMessage={errors.invalid_phone ? t('invalidPhone') : null}>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={30}
                className="slx-form__labeled-input"
                placeholder={t('phonePlaceholder')}
                value={phone}
                onChange={(event) => {
                  setPhone(sanitizePhone(event.target.value));
                  setErrors((previous) => ({ ...previous, phone: false, invalid_phone: false }));
                }}
              />
            </LabeledField>
            {/* Figma: il telefono occupa mezza riga, sotto il giorno */}
            <div className="slx-form__field slx-callback__spacer" />
          </div>
        </div>

        <p className="slx-form__section-description">{t('note')}</p>

        {submitError && <p className="slx-form__error">{submitError}</p>}

        <div className="slx-callback__footer">
          <button type="submit" className="slx-pill" disabled={submitting}>{t('submit')}</button>
        </div>
      </form>
    </div>
  );
};

export default SubmissionListWithdrawalCallbackModal;
